// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import PostCard from '../components/posts/PostCard';
import PostForm from '../components/posts/PostForm';
import FeedTabs from '../components/feeds/FeedTabs';
import ReelFeed from '../components/reels/ReelFeed'; // Import your existing ReelFeed
import '../styles/home.css';

const HomePage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('home');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const fetchFeed = async (feedType = 'home', pageNum = 1) => {
    try {
      setLoading(true);
      
      // Handle trending reels differently
      if (feedType === 'trending-reels') {
        const response = await api.get('/api/feeds/trending-reels', {
          params: { page: pageNum, limit: 10 }
        });
        
        const { posts: newPosts, pagination } = response.data;
        
        if (pageNum === 1) {
          setPosts(newPosts);
        } else {
          setPosts(prev => [...prev, ...newPosts]);
        }
        
        setHasMore(pagination.page < pagination.pages);
        return;
      }
      
      // Handle regular feeds (home/global)
      const response = await api.get(`/api/feeds/${feedType}`, {
        params: { page: pageNum, limit: 10 }
      });
      
      const { posts: newPosts, pagination } = response.data;
      
      if (pageNum === 1) {
        // Reset feed
        setPosts(newPosts);
      } else {
        // Append to existing feed
        setPosts(prev => [...prev, ...newPosts]);
      }
      
      // Check if there are more posts to load
      setHasMore(pagination.page < pagination.pages);
      
    } catch (err) {
      console.error('Error fetching feed:', err);
      setError('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchFeed(activeTab);
  }, [activeTab]);
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setHasMore(true);
    setError(''); // Clear any previous errors
  };
  
  // Handle post creation
  const handleNewPost = (newPost) => {
    // Only add to current feed if it's not trending-reels
    if (activeTab !== 'trending-reels') {
      setPosts(prev => [newPost, ...prev]);
    }
  };
  
  // Load more posts
  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchFeed(activeTab, nextPage);
    }
  };

  const handlePostReaction = async (postId, reactionType = 'like') => {
    try {
      const token = localStorage.getItem('token');
      
      const res = await api.post(`/api/posts/${postId}/react`, 
        { reactionType },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );

      if (res.status === 200) {
        const updatedPost = res.data.post;

        setPosts(prevPosts =>
          prevPosts.map(post =>
            post.id === updatedPost.id
              ? { ...post, ...updatedPost }
              : post
          )
        );
      }
    } catch (error) {
      console.error('Error reacting to post:', error);
    }
  };

  const handlePostDelete = (postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId));
  };

  // Render trending reels view
    if (activeTab === 'trending-reels') {
    return (
      <div className="home-page">
        <div className="home-header">
          <FeedTabs activeTab={activeTab} onTabChange={handleTabChange} />
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {/* Simply pass the feedType, let ReelFeed handle its own data */}
        <ReelFeed feedType="trending" />
      </div>
    );
  }

  // Render regular feed view (home/global)
  return (
    <div className="home-page">
      <div className="home-header">
        <FeedTabs activeTab={activeTab} onTabChange={handleTabChange} />
      </div>
      
      {/* Show post form only for home and global tabs */}
      <PostForm onNewPost={handleNewPost} />
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="posts-container">
        {loading && posts.length === 0 ? (
          <div className="loading">Loading posts...</div>
        ) : posts.length > 0 ? (
          <>
            {posts.map(post => (
              <PostCard 
                key={post.id} 
                post={post} 
                onReaction={handlePostReaction}
                onDelete={handlePostDelete}
              />
            ))}
            
            {hasMore && (
              <button 
                onClick={loadMore} 
                disabled={loading}
                className="load-more-button"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </>
        ) : (
          <div className="empty-state">
            <h3>No posts yet</h3>
            <p>
              {activeTab === 'home' 
                ? 'Follow some users to see their posts here!'
                : 'Be the first to create a post!'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;